import { useState, useMemo, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Trophy, Sparkles, PartyPopper, ArrowRight, Home, Zap, Crown, Sword, Star } from "lucide-react";
import { XPGainAnimation } from "./XPDisplay";

export function WinModal({ 
  isOpen, 
  score, 
  movesLeft, 
  playerName, 
  levelId,
  hasNextLevel,
  onNextLevel, 
  onBackToMap,
  // XP Props
  xpGained,
  previousXP,
  newXP,
  previousLevel,
  newLevel,
  neededXP,
  // Coins Props
  coinsEarned = 0,
  starsEarned = 0,
  // Battle Pass Props
  battlePassXP = 0,
  battlePassBonusXP = 0,
  isPremium = false,
  // Item Drops
  itemDrops = [],
  // Sword Bonus Props
  swordBonus = 0,
  swordInfo = null,
  movesUsed = 0,
  swordMultiplier = 0,
}) {
  const bonusPoints = movesLeft * 50;
  const totalScore = score + bonusPoints + swordBonus;
  const [xpAnimationComplete, setXpAnimationComplete] = useState(false);
  const [displayScore, setDisplayScore] = useState(0);
  const [starsRevealed, setStarsRevealed] = useState(0);

  // Memoize confetti positions
  const confettiPieces = useMemo(() => {
    const colors = ['#ff6b6b', '#feca57', '#48dbfb', '#ff9ff3', '#1dd1a1', '#5f27cd', '#ff9f43'];
    return Array.from({ length: 24 }, (_, i) => ({
      left: `${(i * 4.3) % 100}%`,
      delay: `${(i * 0.12)}s`,
      duration: `${2 + (i % 4) * 0.5}s`,
      color: colors[i % 7],
      rotation: (i * 137) % 360,
      size: i % 3 === 0 ? 'w-3 h-1' : 'w-2 h-2',
      shape: i % 3 === 0 ? '' : 'rounded-full'
    }));
  }, []);

  // Rolling score counter
  useEffect(() => {
    if (!isOpen) return;
    let start = 0;
    const end = totalScore;
    const duration = 1200;
    const startTime = Date.now();
    const tick = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayScore(Math.round(end * eased));
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [isOpen, totalScore]);

  // Star reveal animation
  useEffect(() => {
    if (!isOpen) return;
    const maxStars = starsEarned || (movesLeft > 5 ? 3 : movesLeft > 0 ? 2 : 1);
    const timers = [];
    for (let i = 0; i < maxStars; i++) {
      timers.push(setTimeout(() => setStarsRevealed(i + 1), 400 + i * 300));
    }
    return () => timers.forEach(clearTimeout);
  }, [isOpen, starsEarned, movesLeft]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-3">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80" />
      
      {/* Confetti Animation */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-[201]">
        {confettiPieces.map((c, i) => (
          <div
            key={i}
            className={`absolute ${c.size} ${c.shape}`}
            style={{
              left: c.left,
              top: '-20px',
              backgroundColor: c.color,
              transform: `rotate(${c.rotation}deg)`,
              animation: `confetti-fall ${c.duration} linear ${c.delay} infinite`,
            }}
          />
        ))}
      </div>
      
      {/* Modal Content */}
      <div className="relative z-[202] w-full max-w-sm bg-white dark:bg-gray-900 rounded-xl border-2 border-success/30 shadow-xl max-h-[85vh] overflow-y-auto p-4 animate-modal-pop-in">
        {/* Stars */}
        <div className="flex justify-center gap-2 mb-2">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`transition-all duration-500 ${starsRevealed >= s ? 'scale-100 opacity-100' : 'scale-0 opacity-0'}`}
              style={{ transitionDelay: `${(s - 1) * 0.15}s` }}
            >
              <Star className={`w-8 h-8 ${starsRevealed >= s ? 'text-yellow-400 fill-yellow-400 drop-shadow-[0_0_8px_rgba(250,204,21,0.6)]' : 'text-gray-600'}`} />
            </div>
          ))}
        </div>
        
        {/* Header */}
        <div className="text-center mb-3">
          <div className="flex justify-center mb-2">
            <div className="w-12 h-12 rounded-full bg-success/20 flex items-center justify-center">
              <PartyPopper className="w-6 h-6 text-success" />
            </div>
          </div>
          <h2 className="font-display text-xl font-bold text-success">
            Level {levelId || 1} Complete! 🎉
          </h2>
          <p className="font-body text-sm text-muted-foreground">
            Amazing job{playerName ? `, ${playerName}` : ''}!
          </p>
        </div>

        {/* Score Breakdown - Compact */}
        <div className="space-y-1.5 mb-3">
          <div className="flex justify-between items-center px-3 py-1.5 bg-muted rounded-lg text-sm">
            <span className="text-muted-foreground">Game Score</span>
            <span className="font-bold">{score}</span>
          </div>
          
          <div className="flex justify-between items-center px-3 py-1.5 bg-warning/10 rounded-lg text-sm">
            <span className="text-warning-foreground">Moves Bonus ({movesLeft} × 50)</span>
            <span className="font-bold text-warning">+{bonusPoints}</span>
          </div>
          
          {/* Sword Bonus - Inline */}
          {swordBonus > 0 && swordInfo && (
            <div className="flex justify-between items-center px-3 py-1.5 bg-blue-500/10 rounded-lg text-sm">
              <span className="text-blue-600 dark:text-blue-300 flex items-center gap-1">
                <Sword className="w-3 h-3" />
                {swordInfo.emoji} {swordInfo.name} ({movesUsed} × {swordMultiplier})
              </span>
              <span className="font-bold text-blue-500">+{swordBonus}</span>
            </div>
          )}
          
          {/* Total Score - Highlighted */}
          <div className="flex justify-between items-center px-3 py-2 bg-success/10 rounded-lg border border-success/20">
            <span className="font-semibold text-success">Total Score</span>
            <div className="flex items-center gap-1.5">
              <Trophy className="w-4 h-4 text-warning" />
              <span className="font-bold text-lg text-success">{displayScore.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* Rewards Row - Coins & Battle Pass XP */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          {/* Coins */}
          {coinsEarned > 0 && (
            <div className="flex items-center gap-2 p-2 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
              <span className="text-lg">🪙</span>
              <div>
                <div className="text-xs text-yellow-600 dark:text-yellow-400">Coins</div>
                <div className="font-bold text-yellow-700 dark:text-yellow-300">+{coinsEarned}</div>
              </div>
            </div>
          )}
          
          {/* Battle Pass XP - Shows bonus for premium users */}
          {battlePassXP > 0 && (
            <div className="flex items-center gap-2 p-2 bg-indigo-500/10 rounded-lg border border-indigo-500/20">
              <Zap className="w-5 h-5 text-indigo-500" />
              <div>
                <div className="text-xs text-indigo-600 dark:text-indigo-400 flex items-center gap-1">
                  BP XP {isPremium && <Crown className="w-3 h-3 text-yellow-400" />}
                </div>
                <div className="font-bold text-indigo-700 dark:text-indigo-300">
                  +{battlePassXP}
                  {battlePassBonusXP > 0 && (
                    <span className="text-xs text-yellow-500 ml-1">(+{battlePassBonusXP} bonus)</span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* XP Gain - Animated */}
        {xpGained > 0 && (
          <div className="mb-3 pt-2 border-t border-border">
            <XPGainAnimation
              xpGained={xpGained}
              previousXP={previousXP}
              newXP={newXP}
              previousLevel={previousLevel}
              newLevel={newLevel}
              neededXP={neededXP}
              onAnimationComplete={() => setXpAnimationComplete(true)}
            />
          </div>
        )}

        {/* Item Drops - Compact */}
        {itemDrops && itemDrops.length > 0 && (
          <div className="mb-3 pt-2 border-t border-border">
            <div className="text-center mb-1">
              <span className="text-xs font-bold text-amber-300">🎁 ITEM DROP!</span>
            </div>
            <div className="space-y-1">
              {itemDrops.map((drop, index) => (
                <div 
                  key={index}
                  className={`flex items-center justify-between p-2 rounded-lg border ${
                    drop.rarity === 'legendary' 
                      ? 'bg-yellow-500/20 border-yellow-400' 
                      : drop.rarity === 'epic'
                      ? 'bg-purple-500/20 border-purple-400'
                      : 'bg-blue-500/20 border-blue-400'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{drop.emoji}</span>
                    <div>
                      <div className="font-bold text-white text-sm">{drop.name}</div>
                      <div className="text-xs text-gray-300">{drop.description}</div>
                    </div>
                  </div>
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold uppercase ${
                    drop.rarity === 'legendary' 
                      ? 'bg-yellow-500 text-yellow-900' 
                      : drop.rarity === 'epic'
                      ? 'bg-purple-500 text-white'
                      : 'bg-blue-500 text-white'
                  }`}>
                    {drop.rarity}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Buttons - Compact */}
        <div className="space-y-2">
          {hasNextLevel ? (
            <>
              <Button 
                onClick={onNextLevel}
                className="w-full font-semibold bg-success hover:bg-success/90 text-success-foreground"
              >
                <ArrowRight className="w-4 h-4 mr-2" />
                Next Level
              </Button>
              
              <Button 
                onClick={onBackToMap}
                variant="outline"
                className="w-full font-semibold"
              >
                <Home className="w-4 h-4 mr-2" />
                Level Map
              </Button>
            </>
          ) : (
            <>
              <div className="p-3 bg-gradient-to-r from-warning/20 to-success/20 rounded-lg text-center border border-warning/30">
                <Sparkles className="w-6 h-6 text-warning mx-auto mb-1" />
                <p className="font-bold text-sm">All Levels Complete!</p>
              </div>
              
              <Button 
                onClick={onBackToMap}
                className="w-full font-semibold bg-primary hover:bg-primary/90"
              >
                <Home className="w-4 h-4 mr-2" />
                Back to Map
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
